import { useState } from "react";
import { UserPlus, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { setFoundationCustomerNumber, syncFoundationRef } from "@/lib/api";
import FoundationRefPicker from "./FoundationRefPicker";

// "New customer — not yet in Foundation" flag + in-place fix: pick the
// Foundation customer # (searchable, from synced data) or refresh the synced
// list after creating the customer in Foundation. The number is written to the
// APP CUSTOMER, so every future WO for them resolves.
export default function FoundationNewCustomerNotice({ customer, onChanged, disabled, testIdPrefix = "sp", allowOverride = false }) {
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [typed, setTyped] = useState("");
  if (!customer) return null;
  const isNew = customer.is_new;
  if (!isNew && !allowOverride) return null;

  const pick = async (code) => {
    if (!code) return;
    setBusy(true);
    try {
      const { data } = await setFoundationCustomerNumber(customer.client_id, code);
      const msg = `${customer.client_name} → Foundation customer ${data.customer_number}`
        + (data.reflagged_work_orders ? ` · ${data.reflagged_work_orders} pushed job(s) re-flagged for update` : "");
      if (data.warning) toast.warning(msg, { description: data.warning, duration: 8000 });
      else toast.success(msg);
      setTyped("");
      onChanged?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not set the Foundation customer #");
    } finally { setBusy(false); }
  };

  const refresh = async () => {
    setSyncing(true);
    try {
      await syncFoundationRef("customers");
      toast.success("Foundation customers refreshed");
      onChanged?.(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally { setSyncing(false); }
  };

  return (
    <div className={`rounded-md border p-2 space-y-1.5 ${isNew ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`}
         data-testid={`${testIdPrefix}-new-customer`}>
      <div className="flex items-center gap-1.5 font-semibold text-amber-900">
        <UserPlus className="w-3.5 h-3.5" />
        {isNew ? "New customer — not yet in Foundation" : "Foundation customer #"}
        <span className="font-normal text-slate-600">· {customer.client_name}</span>
      </div>
      {isNew && (
        <div className="text-amber-900" data-testid={`${testIdPrefix}-new-customer-reason`}>
          {customer.reason} <strong>@customer_no is withheld</strong> — the rest of the job still pushes, and Foundation applies its own default.
          Create the customer in Foundation, refresh, then set the number here.
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-slate-500">Set Foundation customer #:</span>
        <FoundationRefPicker listId="customers" value={customer.foundation_code || customer.customer_number || null}
                             onChange={pick} disabled={disabled || busy} allowClear={false}
                             placeholder="Search Foundation customers…" testId={`${testIdPrefix}-customer-picker`} />
        {busy && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
        <span className="text-slate-400">or type it</span>
        <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Customer #"
               onKeyDown={(e) => { if (e.key === "Enter" && typed.trim()) pick(typed.trim()); }}
               disabled={disabled || busy}
               className="h-7 w-28 px-2 text-xs font-mono border border-slate-300 rounded-md bg-white disabled:opacity-50"
               data-testid={`${testIdPrefix}-customer-manual-input`} />
        <button type="button" onClick={() => typed.trim() && pick(typed.trim())} disabled={disabled || busy || !typed.trim()}
                className="h-7 px-2 text-[11px] rounded-md border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50"
                data-testid={`${testIdPrefix}-customer-manual-save`}>
          Save
        </button>
        <button type="button" onClick={refresh} disabled={syncing}
                className="text-[11px] text-blue-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50"
                data-testid={`${testIdPrefix}-customer-refresh`}>
          <RefreshCw className={`w-3 h-3 ${syncing ? "animate-spin" : ""}`} /> Refresh from Foundation
        </button>
      </div>
    </div>
  );
}
